/** Admin API client: session cookie + double-submit CSRF header (ADR-0007); errors are RFC 7807 problems. */
import type { ProblemDetails, UploadTransport } from '@uatp/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Partial<ProblemDetails> | null,
  ) {
    super(problem?.detail ?? `Request failed (${status})`);
  }
  get code(): string | undefined {
    return this.problem?.code;
  }
  /** Field errors keyed by the last path segment (e.g. body.name → name). */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem?.errors ?? []) out[e.path.split('.').slice(1).join('.') || e.path] = e.message;
    return out;
  }
}

let csrfToken = '';
let onUnauthorized: (() => void) | null = null;

export function setCsrfToken(token: string): void {
  csrfToken = token;
}
export function setUnauthorizedHandler(cb: () => void): void {
  onUnauthorized = cb;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export async function api<T>(method: Method, path: string, body?: unknown, init: { signal?: AbortSignal } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(method !== 'GET' ? { 'x-csrf-token': csrfToken } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: init.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, { detail: "Can't reach the server. Check your connection and try again." });
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = new ApiError(res.status, (data as Partial<ProblemDetails>) ?? null);
    if (res.status === 401 && err.code !== 'invalid_credentials' && err.code !== 'mfa_invalid' && err.code !== 'locked') onUnauthorized?.();
    throw err;
  }
  return data as T;
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>('GET', path, undefined, { signal });
export const post = <T>(path: string, body: unknown = {}) => api<T>('POST', path, body);
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body);
export const del = <T>(path: string) => api<T>('DELETE', path);

/** Builds a query string, skipping empty values; arrays are comma-joined. */
export function qs(params: Record<string, string | number | boolean | string[] | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    u.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export const adminUploads: UploadTransport = {
  init: (body) => post('/uploads/init', body),
  complete: (uploadId) => post(`/uploads/${uploadId}/complete`),
};

/** Triggers a browser download for an authenticated GET (CSV/XLSX exports). */
export async function download(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => null)) as Partial<ProblemDetails> | null);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
